import "font-awesome/css/font-awesome.css";

import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import { NgModule, NO_ERRORS_SCHEMA } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { HttpModule } from "@angular/http";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { RouterModule } from "@angular/router";
import { CurrencyMaskModule } from "ng2-currency-mask";
import { CookieService } from "ngx-cookie-service";
import {
  AutoCompleteModule,
  BlockUIModule,
  CalendarModule,
  CheckboxModule,
  DataListModule,
  DataTableModule,
  DialogModule,
  DropdownModule,
  InputMaskModule,
  MultiSelectModule,
  OverlayPanelModule,
  PanelModule,
  SharedModule,
} from "primeng/primeng";

import { environment } from "../environments/environment";
import { AppComponent } from "./app.component";
import { ConfirmDepositComponent } from "./components/confirm-deposit/confirm-deposit.component";
import { DepositSearchComponent } from "./components/deposit-search/deposit-search.component";
import { EventSearchComponent } from "./components/event-search/event-search.component";
import { EventSearchApplicationSelectionComponent } from "./components/event-search/eventsearch-applicationselection/eventsearch-applicationselection.component";
import { EventSearchGLPolicySelectionComponent } from "./components/event-search/eventsearch-gl-policy-selection/eventsearch-gl-policy-selection.component";
import { EventSearchManualApplicationComponent } from "./components/event-search/eventsearch-manualapplication/eventsearch-manualapplication.component";
import { EventSearchPostedClaimGLAdjustmentComponent } from "./components/event-search/eventsearch-postedclaim-gladjustment/eventsearch-postedclaim-gladjustment.component";
import { EventSearchPostedClaimReversalComponent } from "./components/event-search/eventsearch-postedclaimreversal/eventsearch-postedclaimreversal.component";
import { EventSearchPostedRemittanceGLAdjustmentComponent } from "./components/event-search/eventsearch-postedremittance-gladjustment/eventsearch-postedremittance-gladjustment.component";
import { EventSearchPostedRemittanceSEIDUpdateComponent } from "./components/event-search/eventsearch-postedremittance-seidupdate/eventsearch-postedremittance-seidupdate.component";
import { EventSearchRemittanceSplitComponent } from "./components/event-search/eventsearch-remittancesplit/eventsearch-remittancesplit.component";
import { EventSearchReversalTransactionComponent } from "./components/event-search/eventsearch-reversaltransaction/eventsearch-reversaltransaction.component";
import { FooterComponent } from "./components/footer/footer.component";
import { HeaderComponent } from "./components/header/header.component";
import { HealthCheckComponent } from "./components/healthcheck/healthcheck.component";
import { LandingPageComponent } from "./components/landing-page/landing-page.component";
import { ModalConfirmComponent } from "./components/modal-confirm/modal-confirm.component";
import { PageNotFoundComponent } from "./components/page-not-found/page-not-found.component";
import { AdjustmentCodeComponent } from "./components/remittance-entry/adjustment-code/adjustment-code.component";
import { CheckDetailComponent } from "./components/remittance-entry/check-detail/check-detail.component";
import { CreateRemittanceComponent } from "./components/remittance-entry/create-remittance/create-remittance.component";
import { RemittanceEntryComponent } from "./components/remittance-entry/remittance-entry.component";
import { RemittanceStageComponent } from "./components/remittance-entry/remittance-stage/remittance-stage.component";
import { RemittanceSummaryComponent } from "./components/remittance-entry/remittance-summary/remittance-summary.component";
import { StoreIdentifierComponent } from "./components/remittance-entry/store-identifier/store-identifier.component";
import { SectionsComponent } from "./components/sections/sections.component";
import { UserDetailsComponent } from "./components/user-details/user-details.component";
import { AuthGuard } from "./guards/auth.guard";
import { FeatureGuard } from "./guards/feature.guard";
import { HomeComponent } from "./home/home.component";
import { HomeModule } from "./home/home.module";
import { AccountingPipe } from "./pipes/accounting.Pipe";
import { CustomCurrencyPipe } from "./pipes/customCurrencyPipe.pipe";
import { DatexPipe } from "./pipes/datex.pipe";
import { EnumFormatterPipe } from "./pipes/enum-formatter.pipe";
import { FilterPipe } from "./pipes/filter.pipe";
import * as routes from "./routes";
import { BaseService } from "./services/api-base.service";
import { AppInsightsService } from "./services/app-insights.service";
import { ApplicationService } from "./services/application.service";
import { AuthorizationService } from "./services/authorization.service";
import { CalendarService } from "./services/calendar-api.service";
import { CarrierService } from "./services/carrier-api.service";
import { DepositService } from "./services/deposit-api.service";
import { DepositSettingsService } from "./services/depositSettings-api.service";
import { GLPolicyService } from "./services/gl-policy.service";
import { GlobalFilterService } from "./services/global-filter.service";
import { GoogleAnalyticsService } from "./services/google-analytics.service";
import { ReconApiSearchService } from "./services/recon-api.search.service";
import { ReconApiService } from "./services/recon-api.service";
import { CreateRemittanceService } from "./services/remittance-entry/create-remittance.service";
import { RemittanceEntryService } from "./services/remittance-entry/remittance-entry.service";
import { RemittanceStageService } from "./services/remittance-entry/remittance-stage.service";
import { RemittanceUnapprovedService } from "./services/remittance-unapproved.service";
import { RemittanceSummaryService } from "./services/remittance-entry/remittance-summary.service";
import { StoreIdentifierService } from "./services/remittance-entry/store-identifier.service";
import { UserDetailsService } from "./services/user-details.service";
import { SettingsComponent } from "./settings/settings.component";
import { SettingsModule } from "./settings/settings.module";
import { UnapprovedRemittancesComponent } from "./components/deposit-search/unapproved-remittances/unapproved-remittances.component";
import { CreateZeroDollarRemittanceService } from "./services/remittance-entry/create-zerodollar-remittance.service";
import { CreateZeroDollarCheckComponent } from "./components/deposit-search/create-zerodollar-check/create-zerodollar-check.component";
import { NoAccessComponent } from "./components/no-access/no-access.component";
import { TokenInterceptorService } from "./services/token-interceptor.service";

@NgModule({
  schemas: [NO_ERRORS_SCHEMA],
  imports: [
    RouterModule.forRoot(routes.appRoutes, {
      enableTracing: !environment.production,
      useHash: true /* For old IE support */,
    }),
    BrowserModule,
    HttpModule,
    HomeModule,
    FormsModule,
    ReactiveFormsModule,
    DataTableModule,
    CheckboxModule,
    SharedModule,
    MultiSelectModule,
    PanelModule,
    BrowserAnimationsModule,
    CalendarModule,
    InputMaskModule,
    OverlayPanelModule,
    SettingsModule,
    DialogModule,
    HttpClientModule,
    BlockUIModule,
    DataListModule,
    AutoCompleteModule,
    DropdownModule,
    CurrencyMaskModule,
  ],
  declarations: [
    AccountingPipe,
    AppComponent,
    HeaderComponent,
    FooterComponent,
    PageNotFoundComponent,
    HealthCheckComponent,
    SectionsComponent,
    HomeComponent,
    LandingPageComponent,
    SettingsComponent,
    EventSearchComponent,
    ConfirmDepositComponent,
    CustomCurrencyPipe,
    DatexPipe,
    FilterPipe,
    EnumFormatterPipe,
    EventSearchApplicationSelectionComponent,
    EventSearchManualApplicationComponent,
    EventSearchReversalTransactionComponent,
    EventSearchRemittanceSplitComponent,
    UserDetailsComponent,
    EventSearchPostedClaimReversalComponent,
    DepositSearchComponent,
    RemittanceEntryComponent,
    RemittanceStageComponent,
    CheckDetailComponent,
    RemittanceSummaryComponent,
    CreateRemittanceComponent,
    StoreIdentifierComponent,
    AdjustmentCodeComponent,
    EventSearchPostedClaimGLAdjustmentComponent,
    EventSearchGLPolicySelectionComponent,
    EventSearchPostedRemittanceGLAdjustmentComponent,
    EventSearchPostedRemittanceSEIDUpdateComponent,
    ModalConfirmComponent,
    UnapprovedRemittancesComponent,
    CreateZeroDollarCheckComponent,
    NoAccessComponent,
  ],
  exports: [FormsModule, ReactiveFormsModule],
  providers: [
    ReconApiService,
    CookieService,
    BaseService,
    AuthorizationService,
    AuthGuard,
    FeatureGuard,
    GlobalFilterService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptorService,
      multi: true,
    },
    ReconApiSearchService,
    DepositService,
    DepositSettingsService,
    ApplicationService,
    AppInsightsService,
    CalendarService,
    CarrierService,
    UserDetailsService,
    RemittanceStageService,
    RemittanceUnapprovedService,
    RemittanceSummaryService,
    CreateRemittanceService,
    StoreIdentifierService,
    RemittanceEntryService,
    GLPolicyService,
    GoogleAnalyticsService,
    CreateZeroDollarRemittanceService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(private cookieService: CookieService) {
    const self = this;
    $.ajaxSetup({
      beforeSend: (xhr: JQueryXHR): void => {
        if (self.cookieService.check("id_token")) {
          xhr.setRequestHeader(
            "Authorization",
            `Bearer ${self.cookieService.get("id_token")}`
          );
        }
      },
    } as JQueryAjaxSettings);
  }
}



// WEBPACK FOOTER //
// ./src/app/app.module.ts